import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const IchDankDirLieberHerreViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Ich dank dir, lieber Herre
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV347/bwv347/bwv347-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Ich dank dir, lieber Herre.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4290">
      <Navbar data-id="element-4291" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4292">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4293">
          <img src="https://images.unsplash.com/photo-1519683109079-d5f539e1542f?w=400&h=300&fit=crop" alt="Ich dank dir, lieber Herre" className="w-full h-64 md:h-80 object-cover" data-id="element-4294" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4295" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4296">
            <div className="max-w-7xl mx-auto" data-id="element-4297">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4298">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4299" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4300">
                Ich dank dir, lieber Herre
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4301">
                BWV 347, Chorale
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4302">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4303">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4304">
          <div className="flex items-center space-x-2" data-id="element-4305">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4306">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4307" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4308">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4309">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4310" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4311">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4312">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4313" />
              <span className="hidden sm:inline" data-id="element-4314">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4315">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4316" />
              <span className="hidden sm:inline" data-id="element-4317">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4318">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4319">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4320">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Ich dank dir, lieber Herre Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4321" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4322">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4323">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4324">
            <div data-id="element-4325">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4326">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4327">
                <div data-id="element-4328">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4329">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4330">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4331">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4332">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4333">
                    18th Century
                  </dd>
                </div>
                <div data-id="element-4334">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4335">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4336">
                    Chorale
                  </dd>
                </div>
                <div data-id="element-4337">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4338">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4339">
                    BWV 347
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4340">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4341">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4342">
                This sacred chorale demonstrates Bach's skill in creating
                expressive four-part harmony within the Lutheran tradition. The
                piece features clear voice leading and thoughtful harmonic
                progressions that support the devotional text. Ideal for
                developing an understanding of Baroque harmonic language and
                part-writing, this chorale exemplifies the balance between
                contrapuntal independence and harmonic unity that characterizes
                Bach's sacred works.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4343" />
    </div>;
};